import { useState } from 'react';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { ProgressDots } from '@/components/shared/progressDots';
import { QuantityStepper } from '@/components/ui/quantity-stepper';
import { BookingCancelSheet } from '@/components/shared/booking/actions/cancel-sheet';
import { MewsWhichSheet } from './sheets/which';
import { useApp } from '@/lib/core/providers/appProvider';
import { getFirstName } from '@/lib/core/utils';
import { cn } from '@/lib/core/utils/cn';
import { useClientTranslation } from '@/lib/core/i18n';
import { formatAgeRange } from '@/services/platform/data/booking/mews';
import type { MewsAllocation, MewsCategoryItem } from '@/services/platform/data/booking/mews/types';
import type { MewsRateAndDistributionProps } from './types';
import { SharedUnitLabels, SUNBED_LABELS } from '../labels';

// ── Helpers ───────────────────────────────────────────────────────────────────

function rebuildGuestDistribution(
  current: MewsAllocation['guestDistribution'],
  newQty: number
): MewsAllocation['guestDistribution'] {
  return Array.from({ length: newQty }, (_, i) => ({
    unitIndex: i,
    adults: current[i]?.adults ?? 1,
    children: current[i]?.children ?? 0,
    pricePerDay: current[i]?.pricePerDay,
  }));
}

// ── MewsRateAndDistribution ───────────────────────────────────────────────────────

export function MewsRateAndDistribution({
  cmsProps,
  booking,
  categories,
  ageConfig,
  guestsMax,
  onAddCategory,
  isAddingCategory,
  onBookingChange,
  onBack,
  onContinue,
  onBackToWhich,
  step,
  labels = SUNBED_LABELS,
  priceUnitLabel,
  addCategoryLabel,
  unitRowVariant,
  allowChildren = true,
}: MewsRateAndDistributionProps) {
  const t = useClientTranslation();
  const { user } = useApp();
  const firstName = getFirstName(user?.name);

  // Age labels — same logic as HowMany sheet, fall back to i18n strings.
  const adultCategory = ageConfig?.ageCategories?.find(c => c.classification === 'Adult');
  const childCategory = ageConfig?.ageCategories?.find(c => c.classification === 'Child');
  const adultsAgesLabel =
    formatAgeRange(adultCategory?.minimalAge, adultCategory?.maximalAge) ?? t(labels.adultsAges);
  const childrenAgesLabel =
    formatAgeRange(childCategory?.minimalAge, childCategory?.maximalAge) ?? t(labels.childrenAges);

  // Two independent vetoes: the Mews configuration decides whether the service prices children at
  // all, and the flow decides whether its unit can hold one. Either one hides the row.
  const showChildren = allowChildren && ageConfig?.childSelectionEnabled !== false;

  const [allocations, setAllocations] = useState<MewsAllocation[]>(booking.allocations);
  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [pendingRemovalIdx, setPendingRemovalIdx] = useState<number | null>(null);
  const [pendingGuestRemoval, setPendingGuestRemoval] = useState<{
    allocIdx: number;
    unitIdx: number;
  } | null>(null);
  const [pendingBackToWhich, setPendingBackToWhich] = useState(false);

  const usedCategoryIds = new Set(allocations.map(a => a.categoryId));
  const remainingCategories = categories.filter(c => !usedCategoryIds.has(c.id));

  const getCategoryPersonCapacity = (categoryId: string) =>
    categories.find(c => c.id === categoryId)?.maxCapacity ?? 99;

  const getCategoryAvailableUnits = (categoryId: string) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.availableUnits ?? 99;
  };

  const totalPrice = allocations.reduce(
    (sum, a) => sum + a.guestDistribution.reduce((gs, g) => gs + (g.pricePerDay ?? a.pricePerDay), 0),
    0
  );

  const handleQtyChange = (allocIdx: number, newQty: number) => {
    if (newQty === 0) {
      setPendingRemovalIdx(allocIdx);
      return;
    }
    setAllocations(prev =>
      prev.map((a, i) =>
        i === allocIdx
          ? {
              ...a,
              quantity: newQty,
              guestDistribution: rebuildGuestDistribution(a.guestDistribution, newQty),
            }
          : a
      )
    );
  };

  const handleConfirmCancel = async () => {
    if (pendingRemovalIdx === null) return;
    const next = allocations.filter((_, i) => i !== pendingRemovalIdx);
    if (next.length > 0) {
      setAllocations(next);
    } else {
      setPendingBackToWhich(true);
    }
  };

  const handleSuccess = () => {
    if (pendingBackToWhich) {
      setPendingBackToWhich(false);
      onBackToWhich();
    }
  };

  const handleGuestChange = (
    allocIdx: number,
    unitIdx: number,
    field: 'adults' | 'children',
    value: number
  ) => {
    const guest = allocations[allocIdx]?.guestDistribution.find(g => g.unitIndex === unitIdx);
    if (!guest) return;

    const newAdults = field === 'adults' ? value : guest.adults;
    const newChildren = field === 'children' ? value : guest.children;

    if (newAdults === 0 && newChildren === 0) {
      setPendingGuestRemoval({ allocIdx, unitIdx });
      return;
    }

    setAllocations(prev =>
      prev.map((a, i) =>
        i === allocIdx
          ? {
              ...a,
              guestDistribution: a.guestDistribution.map(g =>
                g.unitIndex === unitIdx ? { ...g, [field]: value } : g
              ),
            }
          : a
      )
    );
  };

  const handleConfirmGuestRemoval = async () => {
    if (!pendingGuestRemoval) return;
    const { allocIdx, unitIdx } = pendingGuestRemoval;

    const remaining = allocations[allocIdx].guestDistribution.filter(g => g.unitIndex !== unitIdx);

    if (remaining.length === 0) {
      setPendingRemovalIdx(allocIdx);
    } else {
      setAllocations(prev =>
        prev.map((a, i) =>
          i === allocIdx
            ? {
                ...a,
                quantity: remaining.length,
                guestDistribution: remaining.map((g, j) => ({ ...g, unitIndex: j })),
              }
            : a
        )
      );
    }
  };

  const handleAddCategory = (category: MewsCategoryItem) => {
    onAddCategory(category, allocations, updated => {
      setAllocations(updated);
      setIsAddCategoryOpen(false);
    });
  };

  const handleContinue = () => {
    const totalAdults = allocations.reduce(
      (sum, a) => sum + a.guestDistribution.reduce((s, g) => s + g.adults, 0),
      0
    );
    const totalChildren = allocations.reduce(
      (sum, a) => sum + a.guestDistribution.reduce((s, g) => s + g.children, 0),
      0
    );
    onBookingChange({ allocations, adults: totalAdults, children: totalChildren });
    onContinue();
  };

  /**
   * The bookable unit, in both grammatical numbers — every sentence in `SharedUnitLabels` names it
   * through `{unit}`, and which number reads correctly differs per sentence ("Remove massage?" but
   * "Amount of massages").
   */
  const unitOne = t(labels.unit, { count: 1 });
  const unitMany = t(labels.unit, { count: 2 });

  /**
   * Flows that name adults and children separately (the stay flow) set the three split keys; the
   * rest fall back to one combined count, so neither wording was lost when the fork was undone.
   */
  const resolveShortfall = (adults: number, children: number) => {
    const { guestsShortfallAdults, guestsShortfallChildren, guestsShortfallBoth } = labels;
    if (!guestsShortfallAdults || !guestsShortfallChildren || !guestsShortfallBoth) {
      return t(SharedUnitLabels.guestsShortfall, { unit: unitMany, count: adults + children });
    }
    if (adults > 0 && children > 0) return t(guestsShortfallBoth, { adults, children });
    if (adults > 0) return t(guestsShortfallAdults, { count: adults });
    return t(guestsShortfallChildren, { count: children });
  };

  const subtitle = cmsProps?.subtitle ?? t(labels.rateSubtitle);

  return (
    <>
      <div className="pt-safe-area flex h-full flex-col bg-white">
        {/* Header */}
        <header className="flex shrink-0 items-center justify-between px-4 pb-2">
          <button
            type="button"
            aria-label={t('App.Labels.Back')}
            className="flex size-11 cursor-pointer items-center justify-center"
            onClick={onBack}
          >
            <Icon
              name="long-arrow-up-left"
              width={24}
              height={24}
              strokeWidth={2}
              className="text-jncquoi-neutral-1000"
            />
          </button>
          <ProgressDots step={step} />
        </header>

        {/* Scrollable content */}
        <div className="flex flex-1 flex-col overflow-y-auto px-4 pb-[calc((env(safe-area-inset-bottom)+1.5rem)+112px)]">
          {/* Greeting */}
          <div className="pt-6 pb-8">
            {firstName && (
              <p className="font-pp-neue-montreal text-[32px] font-medium text-[#2a2a2a]">{firstName},</p>
            )}
            <p className="font-pp-neue-montreal mt-1 text-[14px] text-[#666]">{subtitle}</p>
          </div>

          {/* AMOUNT OF UNITS */}
          <section className="flex flex-col gap-4">
            <div>
              <p className="font-pp-neue-montreal text-[14px] tracking-[0.5px] text-[#666] uppercase">
                {cmsProps?.infoAmountTitle ?? t(SharedUnitLabels.amountOfUnits, { unit: unitMany })}
              </p>
              {cmsProps?.infoAmountDescription && (
                <p className="font-pp-neue-montreal mt-1 text-[14px] text-[#666]">
                  {cmsProps.infoAmountDescription}
                </p>
              )}
            </div>

            {allocations.map((alloc, allocIdx) => {
              const showWarning =
                alloc.unitsAvailable !== undefined &&
                alloc.unitsRequested !== undefined &&
                alloc.unitsAvailable < alloc.unitsRequested;
              return (
                <div key={alloc.categoryId} className="flex flex-col gap-2">
                  <div className="flex items-center justify-between border-b-[0.5px] border-[#dfdfdf] pb-4">
                    <div>
                      <p className="font-pp-neue-montreal text-[14px] font-semibold text-[#1a1a1a]">
                        {alloc.categoryName}
                      </p>
                      <p className="font-pp-neue-montreal text-[12px] text-[#666]">
                        {alloc.pricePerDay} € {priceUnitLabel ?? t(labels.priceUnit)}
                      </p>
                    </div>
                    <QuantityStepper
                      value={alloc.quantity}
                      min={0}
                      max={getCategoryAvailableUnits(alloc.categoryId)}
                      onChange={v => handleQtyChange(allocIdx, v)}
                    />
                  </div>
                  {showWarning && (
                    <div className="flex items-center gap-2 rounded-[2px] bg-[#fff9e7] px-4 py-2">
                      <Icon
                        name="warning-triangle"
                        width={16}
                        height={16}
                        strokeWidth={1.5}
                        className="shrink-0 text-[#b4870b]"
                      />
                      <p className="font-pp-neue-montreal text-[12px] leading-normal tracking-[0.5px] text-[#666]">
                        {t(labels.categoryAvailabilityWarning, {
                          available: alloc.unitsAvailable,
                          // requested: alloc.unitsRequested,
                        })}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}

            {(() => {
              const hasAnyAvailabilityWarning = allocations.some(
                a =>
                  a.unitsAvailable !== undefined &&
                  a.unitsRequested !== undefined &&
                  a.unitsAvailable < a.unitsRequested
              );
              const allocatedAdults = allocations.reduce(
                (sum, a) => sum + a.guestDistribution.reduce((s, g) => s + g.adults, 0),
                0
              );
              const allocatedChildren = allocations.reduce(
                (sum, a) => sum + a.guestDistribution.reduce((s, g) => s + g.children, 0),
                0
              );
              const missingAdults = Math.max(0, booking.adults - allocatedAdults);
              const missingChildren = Math.max(0, booking.children - allocatedChildren);
              const missingGuests = missingAdults + missingChildren;
              if (missingGuests <= 0 || hasAnyAvailabilityWarning) return null;
              return (
                <div className="flex items-center gap-2 rounded-[2px] bg-[#fff9e7] px-4 py-2">
                  <Icon
                    name="warning-triangle"
                    width={16}
                    height={16}
                    strokeWidth={1.5}
                    className="shrink-0 text-[#b4870b]"
                  />
                  <p className="font-pp-neue-montreal text-[12px] leading-normal tracking-[0.5px] text-[#666]">
                    {resolveShortfall(missingAdults, missingChildren)}
                  </p>
                </div>
              );
            })()}

            {remainingCategories.length > 0 && (
              <button
                type="button"
                disabled={isAddingCategory}
                onClick={() => setIsAddCategoryOpen(true)}
                className="font-pp-neue-montreal flex h-10 w-full cursor-pointer items-center justify-center text-[12px] tracking-[0.5px] text-[#1a1a1a] uppercase disabled:opacity-50"
              >
                {addCategoryLabel ?? t(SharedUnitLabels.addAnotherCategory, { unit: unitOne })}
              </button>
            )}
          </section>

          {/* GUEST DISTRIBUTION */}
          <section className="mt-8 flex flex-col gap-4">
            <div>
              <p className="font-pp-neue-montreal text-[14px] tracking-[0.5px] text-[#666] uppercase">
                {cmsProps?.guestDistributionTitle ?? t(labels.guestDistributionTitle)}
              </p>
              {cmsProps?.guestDistributionSubtitle && (
                <p className="font-pp-neue-montreal mt-1 text-[14px] text-[#666]">
                  {cmsProps.guestDistributionSubtitle}
                </p>
              )}
            </div>

            {(() => {
              const totalGuests = allocations.reduce(
                (sum, a) => sum + a.guestDistribution.reduce((s, g) => s + g.adults + g.children, 0),
                0
              );
              return allocations.map((alloc, allocIdx) => {
                const maxPerUnit = getCategoryPersonCapacity(alloc.categoryId);
                return alloc.guestDistribution.map(guest => {
                  const remainingForUnit =
                    guestsMax != null ? guestsMax - totalGuests + guest.adults + guest.children : null;
                  const adultsMax =
                    remainingForUnit != null
                      ? Math.min(maxPerUnit - guest.children, remainingForUnit - guest.children)
                      : maxPerUnit - guest.children;
                  const childrenMax =
                    remainingForUnit != null
                      ? Math.min(maxPerUnit - guest.adults, remainingForUnit - guest.adults)
                      : maxPerUnit - guest.adults;
                  return (
                    <div key={`${alloc.categoryId}-${guest.unitIndex}`} className="flex flex-col gap-4">
                      <p
                        className={cn(
                          'text-[16px] tracking-[0.25px]',
                          unitRowVariant === 'plain'
                            ? 'font-inter font-semibold text-[#4e4e4e]'
                            : 'font-pp-neue-montreal text-jncquoi-neutral-900 leading-normal font-medium'
                        )}
                      >
                        {t(labels.unit, { count: 1 })} {guest.unitIndex + 1} |{' '}
                        <span className="font-inter text-jncquoi-neutral-900 text-[16px] leading-normal font-semibold tracking-[0.25px]">
                          {alloc.categoryName}
                        </span>
                      </p>

                      {/* Adults row */}
                      <div className="flex items-center justify-between border-b-[0.5px] border-[#dfdfdf] pb-4">
                        <div>
                          <p className="font-pp-neue-montreal text-[14px] font-semibold text-[#1a1a1a]">
                            {t(labels.adults)}
                          </p>
                          <p className="font-pp-neue-montreal text-[12px] text-[#666]">{adultsAgesLabel}</p>
                        </div>
                        <QuantityStepper
                          value={guest.adults}
                          min={0}
                          max={adultsMax}
                          onChange={v => handleGuestChange(allocIdx, guest.unitIndex, 'adults', v)}
                        />
                      </div>

                      {/* Children row — hidden when childSelectionEnabled is false */}
                      {showChildren && (
                        <div className="flex items-center justify-between border-b-[0.5px] border-[#dfdfdf] pb-4">
                          <div>
                            <p className="font-pp-neue-montreal text-[14px] font-semibold text-[#1a1a1a]">
                              {t(labels.children)}
                            </p>
                            <p className="font-pp-neue-montreal text-[12px] text-[#666]">
                              {childrenAgesLabel}
                            </p>
                          </div>
                          <QuantityStepper
                            value={guest.children}
                            min={0}
                            max={childrenMax}
                            onChange={v => handleGuestChange(allocIdx, guest.unitIndex, 'children', v)}
                          />
                        </div>
                      )}
                    </div>
                  );
                });
              });
            })()}
          </section>
        </div>

        {/* Sticky bottom bar */}
        <div className="fixed bottom-0 left-0 w-full border-t-[0.5px] border-[#c8c8c8] bg-white px-4 pt-6 pb-[calc((env(safe-area-inset-bottom)+42px))]">
          <div className="flex items-center justify-between gap-4">
            <Button
              variant="primary-solid"
              palette="dark"
              size="lg"
              className="max-w-68 flex-1"
              onClick={handleContinue}
            >
              {cmsProps?.bookNowButtonLabel ?? t('App.Labels.BookNow')}
            </Button>
            <div className="shrink-0 text-right">
              <p className="font-pp-neue-montreal text-[14px] font-semibold text-[#1a1a1a]">{totalPrice} €</p>
              <p className="font-pp-neue-montreal text-[12px] text-[#666]">{t('App.Labels.TaxesIncluded')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Add category sheet */}
      <MewsWhichSheet
        isOpen={isAddCategoryOpen}
        onClose={() => setIsAddCategoryOpen(false)}
        categories={remainingCategories}
        selected={null}
        onSelect={handleAddCategory}
      />

      {/* Remove category confirmation sheet */}
      <BookingCancelSheet
        open={pendingRemovalIdx !== null}
        onOpenChange={open => {
          if (!open) setPendingRemovalIdx(null);
        }}
        onConfirmCancel={handleConfirmCancel}
        onSuccess={handleSuccess}
        headerTitle={t('App.Labels.Confirmation')}
        title={t(SharedUnitLabels.removeUnitTitle, { unit: unitOne })}
        description={t(SharedUnitLabels.removeUnitDescription, { unit: unitMany })}
        cancelLabel={t('App.Labels.Confirm')}
        keepLabel={t('App.Labels.KeepSelection')}
        successText={t(SharedUnitLabels.unitRemovedToast)}
      />

      {/* Remove an individual unit from the distribution confirmation sheet */}
      <BookingCancelSheet
        open={pendingGuestRemoval !== null}
        onOpenChange={open => {
          if (!open) setPendingGuestRemoval(null);
        }}
        onConfirmCancel={handleConfirmGuestRemoval}
        onSuccess={handleSuccess}
        headerTitle={t('App.Labels.Confirmation')}
        title={t(SharedUnitLabels.removeUnitTitle, { unit: unitOne })}
        description={t(SharedUnitLabels.removeUnitDescription, { unit: unitMany })}
        cancelLabel={t('App.Labels.Confirm')}
        keepLabel={t('App.Labels.KeepSelection')}
        successText={t(SharedUnitLabels.unitRemovedToast)}
      />
    </>
  );
}
